import { DataGrid, GridOverlay } from "@mui/x-data-grid";
import { useEffect } from "react";
import { CustomDataGridWithMultipleFiltersProps } from "../Interfaces/CustomDataGrid.interfaces";
import CustomDataGridFilterPanel from "./CustomDataGridFilterPanel";
import { CustomDataGridFooter } from "./CustomDataGridFooter";
import { customDataGridSlots } from "./CustomDataGridSlots";
import { useDataGridConfig } from "./hooks/useDataGridConfig";
import { useDataGridEventHandlers } from "./hooks/useDataGridEventHandlers";
import { useDataGridFilters } from "./hooks/useDataGridFilters";
import { useDataGridPagination } from "./hooks/useDataGridPagination";
import { useDataGridState } from "./hooks/useDataGridState";
import { useFilteredRows } from "./hooks/useFilteredRows";
import { useQuickFilterEffect } from "./hooks/useQuickFilterEffect";
import { useTableStateUrl } from "./hooks/useTableStateUrl";

export const CustomDataGrid = (props: Readonly<CustomDataGridWithMultipleFiltersProps>) => {
  // State management
  const { apiRef, activeFieldRef, hasRenderedOnce, hasSetInitialQuickFilterRef, isUpdatingQuickFilterRef, timeoutRef } =
    useDataGridState();

  // Custom hooks
  const { getTableStateFromUrl } = useTableStateUrl(!!props.enableUrlParams);
  const { filterModel, handleFilterChange, updateUrlParams } = useDataGridFilters({
    enableUrlParams: !!props.enableUrlParams,
    initialFilterModel: props.filterModel,
    onFilterChange: props.setFilterOptions
  });

  const { paginationModel, handlePaginationChange, resetToFirstPage } = useDataGridPagination({
    enableUrlParams: !!props.enableUrlParams,
    initialPaginationModel: props.paginationOptions,
    onPaginationChange: props.setPaginationOptions
  });

  const gridRows = useFilteredRows(props.rows.values, filterModel);

  // Event handlers
  const { onFilterChange, onSortModelChange } = useDataGridEventHandlers(
    props,
    !!props.enableUrlParams,
    updateUrlParams,
    getTableStateFromUrl,
    isUpdatingQuickFilterRef,
    activeFieldRef,
    handleFilterChange
  );

  // Grid configuration
  const gridConfig = useDataGridConfig(
    props,
    apiRef,
    paginationModel,
    gridRows,
    onFilterChange,
    handlePaginationChange,
    onSortModelChange
  );

  // Effects
  useEffect(() => {
    hasRenderedOnce.current = true;
  }, []);

  useQuickFilterEffect(
    filterModel,
    resetToFirstPage,
    apiRef,
    hasSetInitialQuickFilterRef,
    isUpdatingQuickFilterRef,
    timeoutRef
  );

  // Derived state
  const showExploreDataButtonOnFooter = props.showExploreDataButtonOnFooter ?? true;
  const showOverlay = !props.loading && gridRows.length === 0 && hasRenderedOnce.current;

  // Component helpers
  const displayFilterPanel = () => (
    <CustomDataGridFilterPanel
      apiRef={apiRef}
      filterModel={filterModel}
      onFilterModelChange={handleFilterChange}
      defaultColumnField={activeFieldRef.current}
    />
  );

  const gridSlots = {
    ...customDataGridSlots(displayFilterPanel, filterModel, props),
    noRowsOverlay: () => (showOverlay ? <GridOverlay>No Rows Found</GridOverlay> : null),
    footer: () => (
      <CustomDataGridFooter
        showExploreDataButton={showExploreDataButtonOnFooter}
        kustoLink={props.kustoLink}
        loading={props.loading}
        TabName={props.TabName}
      />
    )
  };

  const slotProps = {
    ...props.slotProps,
    loadingOverlay: {
      ...props.slotProps?.loadingOverlay,
      variant: props.slotProps?.loadingOverlay?.variant ?? "skeleton",
      noRowsVariant: props.slotProps?.loadingOverlay?.noRowsVariant ?? "skeleton"
    }
  };

  return <DataGrid {...gridConfig} slotProps={slotProps} slots={gridSlots} />;
};
